import React, {
  useEffect, useCallback, useState, useMemo, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import MenuItem from '../../../../components/menu-item/menu-item.component';
import MenuItemNested from '../../../../components/menu-item-nested/menu-item-nested.component';
import TextBold from '../../../../components/text-bold/text-bold.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import CheckedIcon from '../../../../icons/checked-icon';
import LocalStorageService from '../../../../services/local-storage-service';
import StructureService from '../../../../services/structure-service';
import FormatService from '../../../../services/format-service';
import useMobileDevice from '../../../../hooks/use-mobile-device';
import useScreenLarge from '../../../../hooks/use-screen-large';
import useScreenMedium from '../../../../hooks/use-screen-medium';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { setPortfolioIdsFilter, setPortfolioGroupIdsFilter } from './portfolio-filter.action';
import { FILTER_VALUE_EMPTY } from '../../../../constants/system/filter-values';

const PortfolioFilter: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ID_SELECT_PORTFOLIO_FILTER: string = 'select-portfolio-filter';
  const SELECTED_PORTFOLIO_LABEL_DEFAULT: string = 'All portfolios';

  const {
    isInline,
    isDisabled,
    portfolioGroupList,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    onPortfolioFilterValuesChange,
    onBeforePortfolioFilterValuesChange,
  } = props;

  const isLargeScreen: boolean = useScreenLarge();
  const isMediumScreen: boolean = useScreenMedium();
  const isSmallScreen: boolean = useScreenSmall();
  const isMobileDevice: boolean = useMobileDevice();
  const dispatch: ReduxDispatch = useDispatch();
  const storedPortfolioIds: number[] = LocalStorageService.getPortfolioFilterIds();
  const storedPortfolioGroupIds: number[] = LocalStorageService.getPortfolioGroupFilterIds();

  const [isMenuOpen, setIsMenuOpen]: [boolean, Function] = useState(false);

  const blankFilterValue: number[] = useMemo((): number[] => [FILTER_VALUE_EMPTY], []);

  const singlePortfolioIdFilter: number = portfolioIdsFilter && portfolioIdsFilter.length
  && portfolioIdsFilter.length === 1 ? portfolioIdsFilter[0] : null;
  const singlePortfolioGroupIdFilter: number = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
  && portfolioGroupIdsFilter.length === 1 ? portfolioGroupIdsFilter[0] : null;
  const isSubGroupFilter: boolean = portfolioGroupList && portfolioGroupList.length > 1;
  const isOneElementFilter: boolean = !isSubGroupFilter && portfolioGroupList && portfolioGroupList[0]
    && portfolioGroupList[0].portfolios.length === 1;
  const oneElementPortfolioName: string | null = isOneElementFilter ? portfolioGroupList[0].portfolios[0].name : null;
  const currentFilterValue: number = !isSubGroupFilter && isOneElementFilter ? portfolioGroupList
    && [portfolioGroupList[0].portfolios[0].id] : ((!isSubGroupFilter && portfolioIdsFilter) || blankFilterValue);
  const selectedPortfolioGroup: PortfolioGroup = singlePortfolioGroupIdFilter && portfolioGroupList
    ? portfolioGroupList.find((portfolioGroupItem: PortfolioGroup): boolean => (
      portfolioGroupItem.id === singlePortfolioGroupIdFilter
    )) : null;
  const selectedPortfolio: Portfolio = singlePortfolioIdFilter
    && portfolioGroupList.reduce((resultPortfolio: Portfolio, portfolioGroupItem: PortfolioGroup): Portfolio => (
      (portfolioGroupItem.portfolios && !!portfolioGroupItem.portfolios.length
        && portfolioGroupItem.portfolios.find((portfolioItem: Portfolio): boolean => (
          portfolioItem.id === singlePortfolioIdFilter
        ))) || resultPortfolio
    ), null);
  const selectedPortfolioGroupName: string | null = selectedPortfolioGroup ? selectedPortfolioGroup.name : null;
  const selectedPortfolioName: string | null = selectedPortfolio ? selectedPortfolio.name : null;
  const selectedPortfolioLabel: string = (!isMenuOpen && (
    singlePortfolioGroupIdFilter || singlePortfolioIdFilter || isOneElementFilter
      ? (selectedPortfolioGroupName || selectedPortfolioName || oneElementPortfolioName) : (
        (portfolioIdsFilter && !!portfolioIdsFilter.length
          && `${portfolioIdsFilter.length} portfolios are selected`)
        || (portfolioGroupIdsFilter && !!portfolioGroupIdsFilter.length
          && `${portfolioGroupIdsFilter.length} groups are selected`)
      )
  )) || SELECTED_PORTFOLIO_LABEL_DEFAULT;
  const filterSelectWidthDefault: string = '65%';
  const filterSelectWidthLarge: string | undefined = isLargeScreen ? '70%' : undefined;
  const filterSelectWidthMedium: string | undefined = isMediumScreen ? '85%' : undefined;
  const filterSelectWidthSmall: string | undefined = isSmallScreen ? '100%' : undefined;
  const filterSelectWidth: string = filterSelectWidthSmall || filterSelectWidthMedium || filterSelectWidthLarge
    || filterSelectWidthDefault;

  const selectedPortfolios: SelectItem[] = portfolioIdsFilter && portfolioIdsFilter.length
    ? portfolioIdsFilter.map((portfolioId: number): SelectItem => ({
      id: portfolioId,
    })) : [];

  const menuItemSinglePortfolioGroupPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: portfolioGroupList && portfolioGroupList.length ? portfolioGroupList[0].portfolios : [],
    selectedItemList: selectedPortfolios,
    onClick: (
      event: React.MouseEvent<Element, MouseEvent>,
      portfolioItem: Portfolio,
    ): void => {
      onPortfolioSelect(portfolioItem.id);
    },
  }), [portfolioGroupList, selectedPortfolios]);

  const renderPortfolioMenuItemList: Function = useCallback((
    portfolioList: Portfolio[],
    withOffset?: boolean,
  ): ReactElement[] => MenuItemList({
    withNoWrapItems: isSmallScreen,
    withMenuItemOffset: withOffset,
    optionItemLabelKey: 'name',
    optionList: portfolioList,
    selectedItemList: selectedPortfolios,
    onClick: (
      event: React.MouseEvent<Element, MouseEvent>,
      portfolioItem: Portfolio,
    ): void => {
      onPortfolioSelect(portfolioItem.id);
    },
  }), [selectedPortfolios, isSmallScreen]);

  const onPortfolioSelect: Function = useCallback((portfolioValue: number | number[]): void => {
    const portfolioGroup: PortfolioGroup = isSubGroupFilter
      ? portfolioGroupList && portfolioGroupList.find((portfolioGroupItem: PortfolioGroup): boolean => (
      !!portfolioGroupItem.portfolios.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioValue)
    )) : null;
    const selectedPortfolioId: number = StructureService.checkIsValueArray(portfolioValue)
      ? (portfolioValue as number[])[(portfolioValue as number[]).length - 1] : portfolioValue as number;
    const selectedPortfolioGroupId: number | undefined = (portfolioGroup && portfolioGroup.id) || undefined;

    if (!selectedPortfolioId && selectedPortfolioId !== FILTER_VALUE_EMPTY && !selectedPortfolioGroupId) {
      return;
    }

    let newPortfolioGroupIdsFilter: number[] | undefined = [];
    let newPortfolioIdsFilter: number[] | undefined = [];
    if (selectedPortfolioId !== FILTER_VALUE_EMPTY) {
      if (selectedPortfolioId) {
        const selectedPortfolioIdsFilter: number[] = portfolioIdsFilter || [];
        const selectedPortfolioIdIndex: number = selectedPortfolioIdsFilter.indexOf(selectedPortfolioId);

        if (selectedPortfolioIdIndex !== FormatService.INDEX_NOT_FOUND) {
          StructureService.deleteListItemByIndex(selectedPortfolioIdsFilter, selectedPortfolioIdIndex);
        }

        newPortfolioIdsFilter = selectedPortfolioIdIndex !== FormatService.INDEX_NOT_FOUND
          ? [...selectedPortfolioIdsFilter] : [...selectedPortfolioIdsFilter, selectedPortfolioId];
      } else if (selectedPortfolioGroupId) {
        const selectedPortfolioGroupIdsFilter: number[] = portfolioGroupIdsFilter || [];
        const selectedPortfolioGroupIdIndex: number = selectedPortfolioGroupIdsFilter.indexOf(selectedPortfolioGroupId);

        if (selectedPortfolioGroupIdIndex !== FormatService.INDEX_NOT_FOUND) {
          StructureService.deleteListItemByIndex(selectedPortfolioGroupIdsFilter, selectedPortfolioGroupIdIndex);
        }

        newPortfolioGroupIdsFilter = selectedPortfolioGroupIdIndex !== FormatService.INDEX_NOT_FOUND
          ? [...selectedPortfolioGroupIdsFilter] : [...selectedPortfolioGroupIdsFilter, selectedPortfolioGroupId];
      }
    }

    if (!newPortfolioIdsFilter.length) {
      newPortfolioIdsFilter = undefined;
    }

    if (!newPortfolioGroupIdsFilter.length) {
      newPortfolioGroupIdsFilter = undefined;
    }

    if (onBeforePortfolioFilterValuesChange) {
      onBeforePortfolioFilterValuesChange(newPortfolioGroupIdsFilter, newPortfolioIdsFilter);
    }

    if (isDisabled) {
      return;
    }

    dispatch(setPortfolioGroupIdsFilter(newPortfolioGroupIdsFilter));
    dispatch(setPortfolioIdsFilter(newPortfolioIdsFilter));

    if (onPortfolioFilterValuesChange) {
      onPortfolioFilterValuesChange(newPortfolioGroupIdsFilter, newPortfolioIdsFilter);
    }
  }, [isSubGroupFilter, portfolioGroupList, isDisabled, portfolioGroupIdsFilter, portfolioIdsFilter]);

  const onPortfolioGroupSelect: Function = useCallback((portfolioGroupValue: string | string[]): void => {
    const selectedPortfolioGroupId: number = StructureService.checkIsValueArray(portfolioGroupValue)
      ? FormatService.parseInt(portfolioGroupValue[portfolioGroupValue.length - 1])
      : FormatService.parseInt(portfolioGroupValue as string);

    if (!selectedPortfolioGroupId && selectedPortfolioGroupId !== FILTER_VALUE_EMPTY) {
      return;
    }

    let newPortfolioGroupIdsFilter: number[] | undefined = [];
    if (selectedPortfolioGroupId !== FILTER_VALUE_EMPTY) {
      const selectedPortfolioGroupIdsFilter: number[] = portfolioGroupIdsFilter || [];
      const selectedPortfolioGroupIdIndex: number = selectedPortfolioGroupIdsFilter.indexOf(selectedPortfolioGroupId);

      if (selectedPortfolioGroupIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(selectedPortfolioGroupIdsFilter, selectedPortfolioGroupIdIndex);
      }

      newPortfolioGroupIdsFilter = selectedPortfolioGroupIdIndex !== FormatService.INDEX_NOT_FOUND
        ? [...selectedPortfolioGroupIdsFilter] : [...selectedPortfolioGroupIdsFilter, selectedPortfolioGroupId];
    }

    if (!newPortfolioGroupIdsFilter.length) {
      newPortfolioGroupIdsFilter = undefined;
    }

    if (onBeforePortfolioFilterValuesChange) {
      onBeforePortfolioFilterValuesChange(newPortfolioGroupIdsFilter, undefined);
    }

    if (isDisabled) {
      return;
    }

    dispatch(setPortfolioGroupIdsFilter(newPortfolioGroupIdsFilter));
    dispatch(setPortfolioIdsFilter(undefined));

    if (onPortfolioFilterValuesChange) {
      onPortfolioFilterValuesChange(newPortfolioGroupIdsFilter, undefined);
    }
  }, [isDisabled, portfolioGroupIdsFilter]);

  const onContextMenu: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  useEffect((): void => {
    if (storedPortfolioIds && !portfolioIdsFilter) {
      setPortfolioIdsFilter(storedPortfolioIds);
    }

    if (storedPortfolioGroupIds && !portfolioGroupIdsFilter) {
      setPortfolioGroupIdsFilter(storedPortfolioGroupIds);
    }
  }, []);

  return (
    <PaddedFlexContainer isDefault={!isInline}>
      {
        portfolioGroupList && !!portfolioGroupList.length && (
          <FieldSelect
            isPlain
            isMultiple
            isBordered
            isOpen={isMenuOpen}
            isDisabled={isOneElementFilter}
            withBlankElement={!isOneElementFilter}
            withBlankItemDefault={!isOneElementFilter}
            blankElementValue={FILTER_VALUE_EMPTY}
            blankElementLabel={(!isMenuOpen && selectedPortfolioLabel) || 'All portfolios'}
            value={currentFilterValue}
            valueRendered={!isSubGroupFilter ? ((): string => selectedPortfolioLabel) : undefined}
            id={ID_SELECT_PORTFOLIO_FILTER}
            width={filterSelectWidth}
            onOpen={(): void => {
              setIsMenuOpen(true);
            }}
            onClose={(): void => {
              setIsMenuOpen(false);
            }}
            onBlankElementPointerDown={(): void => {
              if (isSubGroupFilter) {
                onPortfolioGroupSelect(FILTER_VALUE_EMPTY);
              } else {
                onPortfolioSelect(FILTER_VALUE_EMPTY);
              }
            }}
            onBlankElementContextMenu={onContextMenu}
          >
            {
              isSubGroupFilter ? (
                portfolioGroupList && !!portfolioGroupList.length
                && portfolioGroupList.map((portfolioGroupItem: PortfolioGroup): ReactElement => {
                  const isGroupItemSelected: boolean = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
                    ? portfolioGroupIdsFilter.includes(portfolioGroupItem.id) : false;

                  return (
                    isMobileDevice ? (
                      <div key={portfolioGroupItem.id}>
                        <MenuItem
                          isActive={isGroupItemSelected}
                          withNoWrap={isSmallScreen}
                          value={portfolioGroupItem.id}
                          onClick={(): void => {
                            onPortfolioGroupSelect(portfolioGroupItem.id);
                          }}
                          onContextMenu={onContextMenu}
                        >
                          <TextBold>
                            {portfolioGroupItem.name}
                          </TextBold>
                          {
                            isGroupItemSelected && (
                              <CheckedIcon />
                            )
                          }
                        </MenuItem>
                        {renderPortfolioMenuItemList(portfolioGroupItem.portfolios, true)}
                      </div>
                    ) : (
                      (portfolioGroupItem.portfolios && !!portfolioGroupItem.portfolios.length && (
                        <MenuItemNested
                          isParentMenuOpen={isMenuOpen}
                          isActive={portfolioGroupIdsFilter && !!portfolioGroupIdsFilter.length
                            && !!portfolioGroupIdsFilter.includes(portfolioGroupItem.id)}
                          key={portfolioGroupItem.id}
                          value={portfolioGroupItem.id}
                          label={portfolioGroupItem.name}
                          onClick={(): void => {
                            onPortfolioGroupSelect(portfolioGroupItem.id);
                          }}
                          onContextMenu={onContextMenu}
                        >
                          {renderPortfolioMenuItemList(portfolioGroupItem.portfolios)}
                        </MenuItemNested>
                      )) || (
                        <div key={portfolioGroupItem.id} />
                      )
                    )
                  );
                })
              ) : menuItemSinglePortfolioGroupPortfolioList
            }
          </FieldSelect>
        )
      }
    </PaddedFlexContainer>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    portfolioFilter,
  } = reducers;

  return {
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
  };
};

export default connect(mapState)(PortfolioFilter) as ReactComponent;
